import { AppLayout } from "@/components/AppLayout";
import { useReferral } from "@/hooks/useReferral";
import { useSubscription } from "@/hooks/useSubscription";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Copy, Users, Gift, Crown, Check, Share2 } from "lucide-react";
import { toast } from "sonner";
import { useState } from "react";
import { cn } from "@/lib/utils";

const Referral = () => {
  const { 
    referralCode, 
    referralLink, 
    referrals, 
    isLoading,
    maxReferrals,
    currentReferrals,
    remainingReferrals,
    canRefer,
    totalCreditsEarned
  } = useReferral();
  const { plan } = useSubscription();
  const [copied, setCopied] = useState(false);

  const copyToClipboard = async () => {
    if (referralLink) {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      toast.success("Lien copié !");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareOnWhatsApp = () => {
    if (referralLink) {
      const message = encodeURIComponent(
        `🎨 Rejoins-moi sur Floop ! Crée des images incroyables avec l'IA. Inscris-toi avec mon lien et obtiens 10 crédits gratuits : ${referralLink}`
      );
      window.open(`https://wa.me/?text=${message}`, "_blank");
    }
  };

  const planLimits = {
    free: { max: 10, label: "Gratuit" },
    pro: { max: 200, label: "Pro" },
    business: { max: -1, label: "Studio Max" },
  };

  const currentLimit = planLimits[plan as keyof typeof planLimits] || planLimits.free;

  if (isLoading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="container mx-auto px-4 py-8 max-w-4xl animate-fade-up">
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2 font-display">
            Programme de Parrainage
          </h1>
          <p className="text-muted-foreground">
            Invitez vos amis et gagnez <span className="text-primary font-semibold">2 crédits</span> par inscription
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="bg-card border-border hover-lift">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-primary/20 flex items-center justify-center">
                  <Users className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{currentReferrals}</p>
                  <p className="text-sm text-muted-foreground">Amis invités</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border hover-lift">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-success/20 flex items-center justify-center">
                  <Gift className="w-6 h-6 text-success" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{totalCreditsEarned}</p>
                  <p className="text-sm text-muted-foreground">Crédits gagnés</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border hover-lift">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-accent/20 flex items-center justify-center">
                  <Crown className="w-6 h-6 text-accent" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">
                    {maxReferrals === -1 ? "∞" : remainingReferrals}
                  </p>
                  <p className="text-sm text-muted-foreground">Invitations restantes</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Referral Link Card */}
        <Card className="bg-card border-border mb-8">
          <CardHeader>
            <CardTitle className="text-xl text-foreground">Votre lien de parrainage</CardTitle>
            <CardDescription>
              Partagez ce lien avec vos amis pour gagner des crédits
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input
                value={referralLink || "Chargement..."}
                readOnly
                className="bg-secondary/50 border-border text-foreground font-mono text-sm"
              />
              <Button
                onClick={copyToClipboard}
                variant="outline"
                className="shrink-0"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={copyToClipboard}
                className="flex-1 bg-gradient-floop hover:opacity-90 text-white"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copier le lien
              </Button>
              <Button
                onClick={shareOnWhatsApp}
                variant="outline"
                className="flex-1 border-success text-success hover:bg-success/10"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Partager sur WhatsApp
              </Button>
            </div>

            {referralCode && (
              <p className="text-center text-sm text-muted-foreground">
                Code: <span className="font-mono font-semibold text-primary">{referralCode.code}</span>
              </p>
            )}
          </CardContent>
        </Card>

        {/* Plan Limits */}
        <Card className="bg-card border-border mb-8">
          <CardHeader>
            <CardTitle className="text-xl text-foreground">Limites par plan</CardTitle>
            <CardDescription>
              Passez à un plan supérieur pour inviter plus d'amis
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { name: "Gratuit", max: 10, plan: "free" },
                { name: "Starter", max: 50, plan: "starter" },
                { name: "Pro", max: 200, plan: "pro" },
                { name: "Studio Max", max: -1, plan: "business" },
              ].map((tier) => (
                <div
                  key={tier.name}
                  className={cn(
                    "p-4 rounded-xl border transition-all",
                    plan === tier.plan || (plan === "free" && tier.plan === "free")
                      ? "border-primary bg-primary/10"
                      : "border-border bg-secondary/30"
                  )}
                >
                  <p className="font-semibold text-foreground">{tier.name}</p>
                  <p className="text-2xl font-bold text-primary">
                    {tier.max === -1 ? "Illimité" : `${tier.max} invitations`}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {tier.max === -1 ? "Sans limite" : `Max ${tier.max} personnes`}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Referral History */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-xl text-foreground">Historique des parrainages</CardTitle>
            <CardDescription>
              Vos amis qui se sont inscrits avec votre lien
            </CardDescription>
          </CardHeader>
          <CardContent>
            {referrals && referrals.length > 0 ? (
              <div className="space-y-3">
                {referrals.map((referral) => (
                  <div
                    key={referral.id}
                    className="flex items-center justify-between p-3 rounded-xl bg-secondary/30 border border-border"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                        <Users className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">
                          Ami #{referral.id.slice(0, 8)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(referral.created_at).toLocaleDateString("fr-FR", {
                            day: "numeric",
                            month: "long",
                            year: "numeric"
                          })}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-success/20 text-success text-sm font-medium">
                        <Gift className="w-3 h-3" />
                        +{referral.credits_awarded} crédits
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Aucun parrainage pour l'instant</p>
                <p className="text-sm">Partagez votre lien pour commencer à gagner des crédits !</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
};

export default Referral;
